import React, { useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import { deleteConversation, getConversationList } from '@/api/chat';
import type { ConversationListVo, ConversationRespVo } from '@/api/chat';
import type { ProcessDefinitionList } from '@/api/chat';
import styles from './index.module.scss';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { useChatContext } from '@/pages/chat/context';
import { useThemeImage } from '@/hooks/useThemeImage';
import icon from '@/assets/images/dark-icon-agent@2x.png';

interface LeftProps {
  processList?: ProcessDefinitionList[];
  onNewConversation: () => void;
  active: boolean;
}

const conversationListUpdatedEvent = 'assistant-conversation-list-updated';

const Left: React.FC<LeftProps> = ({ onNewConversation, active, processList }) => {
  const {
    conversation,
    processInfo,
    setProcessInfo,
    setConversation,
    setHistory,
    setTalking,
    config = {},
  } = useChatContext();

  const logo = useThemeImage('logo');

  const [conversationResp, setConversationResp] = useState<ConversationRespVo>();
  const [deletingId, setDeletingId] = useState<number>();

  // 历史记录可能先于智能体列表返回；列表就绪后恢复完整配置。
  useEffect(() => {
    if (processInfo?.agentCode || !processInfo?.id) return;
    const process = processList?.find(item =>
      (item.agentId ?? Number(item.id)) === Number(processInfo.id),
    );
    if (process && process !== processInfo) setProcessInfo?.(process);
  }, [processInfo, processList, setProcessInfo]);

  const { tenantMcid, staffId } = config;
  useRequest(() => getConversationList({ tenantMcid, staffId }), {
    ready: active && !!tenantMcid && !!staffId,
    onSuccess: data => {
      setConversationResp(data);
    },
  });

  useEffect(() => {
    const handleConversationListUpdated = (event: Event) => {
      const nextConversationResp = (event as CustomEvent<ConversationRespVo>).detail;

      if (!nextConversationResp) return;

      setConversationResp(nextConversationResp);
    };

    window.addEventListener(conversationListUpdatedEvent, handleConversationListUpdated);
    return () => {
      window.removeEventListener(conversationListUpdatedEvent, handleConversationListUpdated);
    };
  }, []);

  const handleDeleteConversation = (item: ConversationListVo) => {
    setDeletingId(item.id);
    deleteConversation(item.id)
      .then(() => {
        message.success('删除成功');
        setConversationResp(current => {
          if (!current) return current;
          return {
            ...current,
            records: current.records?.filter(record => record.id !== item.id) ?? [],
            total: Math.max((current.total ?? 0) - 1, 0),
          };
        });

        if (conversation?.id === item.conversationId) {
          setConversation?.(undefined);
          setHistory?.(undefined);
          setTalking?.(false);
        }
      })
      .catch(error => {
        console.error('删除会话失败:', error);
        message.error('删除失败');
      })
      .finally(() => {
        setDeletingId(undefined);
      });
  };

  const conversationList = conversationResp?.records ?? [];
  return (
    <div className={styles.left}>
      <div className={styles.logo}>
        <img src={logo} width={208} />
      </div>
      <div className={styles.button} onClick={onNewConversation}>
        <PlusOutlined />
        开启新对话
      </div>

      <div className={styles.history}>
        <div>历史记录</div>
        {conversationList?.length ? (
          <div className={styles.historyList}>
            {conversationList.map(item => (
              <div
                key={item.id}
                className={`${styles.historyItem} ${
                  conversation?.id === item.conversationId ? styles.selectedHistory : ''
                }`}
                onClick={() => {
                  if (conversation?.id !== item.conversationId) {
                    const itemConv = {
                      id: item.conversationId,
                      name: item.conversationName || '未命名对话',
                      type: item.agentName || '智能体',
                      agentId: item.agentId,
                      agentName: item.agentName,
                    };
                    setConversation?.(itemConv);
                    setProcessInfo?.(processList?.find(process => (process.agentId ?? Number(process.id)) === item.agentId) ?? {
                      id: item.agentId!,
                      agentName: item.agentName,
                    });
                    setHistory?.(itemConv);
                    setTalking?.(true);
                  }
                }}
              >
                <div className={styles.historyItemLabel}>{item.conversationName ?? '新对话'}</div>
                <div className={styles.historyItemType}>
                  <img src={icon} width={12} /> {item.agentName || '智能体'}
                </div>
                <Button
                  className={styles.deleteButton}
                  disabled={deletingId === item.id}
                  onClick={event => {
                    event.stopPropagation();
                    handleDeleteConversation(item);
                  }}
                  aria-label="删除历史记录"
                  icon={<DeleteOutlined />}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className={`${styles.emptyText} text-center text-sm mt-[8px]`}>暂无对话</div>
        )}
      </div>
    </div>
  );
};

export default Left;
